const id = (value) => String(value || "").trim();

export const getRuleField = (rule, side, field) =>
  id(rule?.[`${side}${field}Id`] || rule?.[`${side}_${field.toLowerCase()}_id`]);

export const isIngredientLinkedRule = (rule) => Boolean(getRuleField(rule, "target", "Ingredient"));
export const getLinkedConsumptionMl = (rule) => Number(rule?.consumptionMl ?? rule?.consumption_ml ?? 0);

export const buildIngredientLinkedOption = (rule) => {
  const ingredientId = getRuleField(rule, "target", "Ingredient");
  const consumptionMl = getLinkedConsumptionMl(rule);
  if (!ingredientId || !Number.isFinite(consumptionMl) || consumptionMl <= 0) return null;
  const name = rule.targetIngredientName || rule.target_ingredient_name || "Variedad";
  return {
    id: `ingredient:${ingredientId}`, targetIngredientId: ingredientId,
    relationId: rule.id || "", consumptionMl, name, description: `${consumptionMl} ml por vaso`,
    price: 0, priceMode: "manual", active: rule.targetIngredientActive !== false,
    ingredients: [{ id: ingredientId, name, menuBaseQuantity: 1,
      consumptionQuantity: consumptionMl, consumptionUnit: "ml" }],
  };
};

const productField = (product, field) => {
  if (field === "Product") return id(product?.id || product?.productId);
  return id(
    product?.[`${field.toLowerCase()}Id`] ||
      product?.[`${field.toLowerCase()}_id`] ||
      product?.[field === "Category" ? "categoria" : "subcategoriaId"] ||
      product?.[field === "Category" ? "categoria_id" : "subcategoria_id"]
  );
};

export const matchesLinkedRule = (rule, product, side = "source") => {
  if (!product || rule?.active === false) return false;
  if (side === "target" && isIngredientLinkedRule(rule)) return false;
  const fields = ["Category", "Subcategory", "Product"];
  return fields.some((field) => getRuleField(rule, side, field)) &&
    fields.every((field) => {
      const expected = getRuleField(rule, side, field);
      return !expected || expected === productField(product, field);
    });
};

export const buildLinkedRuleSourceKey = (rule) => {
  const group = getRuleField(rule, "source", "Subcategory") ||
    `category:${getRuleField(rule, "source", "Category")}`;
  const product = getRuleField(rule, "source", "Product");
  return product ? `${group}::product:${product}` : group;
};

// Keep legacy keys stable so existing carts retain their selections.
export const buildLinkedRuleKey = (rule) => {
  if (isIngredientLinkedRule(rule)) {
    return `${buildLinkedRuleSourceKey(rule)}::ingredient:${getRuleField(rule, "target", "Ingredient")}`;
  }
  const key = [
    buildLinkedRuleSourceKey(rule),
    getRuleField(rule, "target", "Category") || "__no-category__",
    getRuleField(rule, "target", "Subcategory") || "__all__",
  ].join("::");
  const product = getRuleField(rule, "target", "Product");
  return product ? `${key}::product:${product}` : key;
};

export const resolveLinkedRuleProducts = (rule, products = [], sourceProductId = "") =>
  isIngredientLinkedRule(rule)
    ? [buildIngredientLinkedOption(rule)].filter((option) => option && option.active && rule.active !== false)
    : products.filter((product) =>
    product.active !== false &&
    productField(product, "Product") !== id(sourceProductId) &&
    matchesLinkedRule(rule, product, "target")
  );

export const indexLinkedRulesByProduct = (rules = [], products = []) =>
  new Map(products.map((product) => [
    productField(product, "Product"),
    rules.filter((rule) => matchesLinkedRule(rule, product)),
  ]));

export const getLinkedOnlyProductIds = (rules = [], products = []) => {
  const hidden = new Set();
  rules.filter((rule) => !isIngredientLinkedRule(rule) && rule.showAsStandalone !== true && rule.show_as_standalone !== true)
    .forEach((rule) => {
      resolveLinkedRuleProducts(rule, products, getRuleField(rule, "source", "Product"))
        .forEach((product) => hidden.add(productField(product, "Product")));
    });
  return hidden;
};
